import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [showQrModal, setShowQrModal] = useState(false);

  // ESC 关闭二维码弹窗
  useEffect(() => {
    if (!showQrModal) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowQrModal(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showQrModal]);

  // 复制链接
  const handleCopyLink = () => {
    const url = window.location.href;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-brand-primary to-brand-secondary">
      {/* 导航栏 */}
      <header className="sticky top-0 z-50 bg-brand-primary/80 backdrop-blur-xl py-1">
        <div className="container mx-auto px-3 md:px-4 flex items-center justify-center">
          {/* 二维码分享按钮 - 左侧 */}
          <button
            onClick={() => setShowQrModal(true)}
            className="p-2 rounded-lg bg-brand-accent/20 hover:bg-brand-accent/30 border border-brand-accent/40 transition-all mr-3"
            aria-label="分享二维码"
          >
            <svg className="w-5 h-5 text-brand-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.01v.01H6V6zm.01 6h.01v.01H6V12zm0 6h.01v.01H6V18zm6-6h.01v.01H12V12zm0 6h.01v.01H12V18z" />
            </svg>
          </button>

          {/* Logo - 居中 */}
          <Link to="/" className="no-underline group">
            <img
              src="/images/zhixiailogo.png"
              alt="纸喜爱LOGO"
              className="h-[80px] md:h-[100px] w-auto object-contain"
            />
          </Link>
        </div>
      </header>

      {/* 主内容区域 */}
      <main className="flex-grow container mx-auto px-4 py-6 md:py-8" id="main-content" role="main">
        {children}
      </main>

      {/* 页脚 */}
      <footer id="about" className="bg-neutral-900/60 border-t border-neutral-800/60 py-6 md:py-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <div className="flex items-center gap-2 md:gap-3 justify-center md:justify-start">
                <div className="w-5 h-5 md:w-6 md:h-6 bg-gradient-to-br from-brand-accent to-brand-accent-light rounded-full shadow-gold-sm"></div>
                <span className="text-base md:text-lg font-bold text-brand-accent font-serif-chinese tracking-wide">
                  纸喜爱二十八星宿
                </span>
              </div>
              <p className="mt-2 md:mt-3 text-neutral-500 text-xs md:text-sm max-w-md">
                基于山西晋城玉皇庙二十八星宿彩塑文化，结合传统演禽法算法，为您提供精准的星宿测算与解读。
              </p>
            </div>
            <div className="text-center md:text-right text-neutral-400 text-xs md:text-sm">
              <p>&copy; {new Date().getFullYear()} 二十八星宿项目. 保留所有权利.</p>
              <p className="mt-1 text-neutral-700">
                藏青 · 墨黑 · 烫金 — 新中式美学
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* 二维码分享弹窗 */}
      {showQrModal && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm qr-modal-backdrop"
          onClick={() => setShowQrModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label="分享二维码"
        >
          <div
            className="bg-brand-primary border border-brand-accent/30 rounded-2xl p-6 md:p-8 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 标题 */}
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-brand-accent font-serif-chinese mb-2">
                分享二十八星宿
              </h3>
              <p className="text-neutral-400 text-sm">
                扫码访问或复制链接分享
              </p>
            </div>

            {/* 二维码 */}
            <div className="flex justify-center mb-6">
              <div className="bg-white p-4 rounded-xl">
                <QRCodeSVG
                  value={window.location.href}
                  size={180}
                  level="M"
                  includeMargin={false}
                />
              </div>
            </div>

            {/* 当前链接 */}
            <div className="mb-6">
              <div className="flex items-center gap-2 bg-neutral-800/60 rounded-lg px-3 py-2 border border-neutral-700">
                <span className="text-neutral-400 text-xs truncate flex-1">
                  {window.location.href}
                </span>
              </div>
            </div>

            {/* 按钮 */}
            <div className="flex gap-3">
              <button
                onClick={handleCopyLink}
                className="flex-1 btn btn-secondary py-3 text-sm flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                复制链接
              </button>
              <button
                onClick={() => setShowQrModal(false)}
                className="flex-1 btn btn-primary py-3 text-sm"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
